/**
 * Search (GraphQL) Controller – scaffold
 *
 * Purpose:
 * - Placeholder for client-side search orchestration using Storefront GraphQL
 * - Will manage URL state, sorting, filtering, pagination, and rendering
 *
 * Current state:
 * - No-op initialization that can run a debug self-test when enabled
 * - UI wiring and rendering will be implemented in subsequent steps
 */
(function searchGraphQLController() {
  'use strict';

  // Short utilities kept local for clarity and isolation.
  function getParam(name) {
    const url = new URL(window.location.href);
    return url.searchParams.get(name);
  }

  function mapSort(sortBy) {
    // Maps Shopify sort_by to Storefront ProductSortKeys and reverse flag
    switch (sortBy) {
      case 'price-ascending':
        return { sortKey: 'PRICE', reverse: false };
      case 'price-descending':
        return { sortKey: 'PRICE', reverse: true };
      case 'best-selling':
        return { sortKey: 'BEST_SELLING', reverse: false };
      case 'created-ascending':
        return { sortKey: 'CREATED_AT', reverse: false };
      case 'created-descending':
        return { sortKey: 'CREATED_AT', reverse: true };
      case 'relevance':
      default:
        // Default to newest first if no sort provided
        return { sortKey: 'CREATED_AT', reverse: true };
    }
  }

  // Debug DOM helpers removed per request; we keep console logging only.

  const PAGE_SIZE = 24;

  function debounce(fn, wait) {
    let t;
    return function debounced(...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function setLoading(isLoading) {
    const wrapper = document.getElementById('product-grid-graphql');
    if (!wrapper) return;
    if (isLoading) {
      wrapper.classList.add('loading');
      wrapper.setAttribute('aria-busy', 'true');
    } else {
      wrapper.classList.remove('loading');
      wrapper.setAttribute('aria-busy', 'false');
    }
  }

  function sanitizeSearchUrl() {
    try {
      const url = new URL(window.location.href);
      // Known Shopify search tracking params and Liquid-specific params to remove
      const stripKeys = ['_pos', '_psq', '_ss', '_v', 'options[prefix]'];
      let changed = false;
      stripKeys.forEach((k) => {
        if (url.searchParams.has(k)) {
          url.searchParams.delete(k);
          changed = true;
        }
      });
      // Keep only q, sort_by, page (anything else gets removed)
      const allow = new Set(['q', 'sort_by', 'page']);
      Array.from(url.searchParams.keys()).forEach((k) => {
        if (!allow.has(k)) {
          url.searchParams.delete(k);
          changed = true;
        }
      });
      if (changed) {
        history.replaceState({}, '', url.toString());
      }
    } catch (_) {
      /* no-op */
    }
  }

  function getNumericProductId(gid) {
    // Extract the numeric ID from a Shopify GID, e.g., gid://shopify/Product/1234567890
    const match = typeof gid === 'string' ? gid.match(/Product\/(\d+)/) : null;
    return match ? match[1] : '';
  }

  function reinitGiveawayGator() {
    try {
      if (window.GiveawayGator && typeof window.GiveawayGator.initialize === 'function') {
        window.GiveawayGator.initialize();
      } else {
        setTimeout(() => {
          if (window.GiveawayGator && typeof window.GiveawayGator.initialize === 'function') {
            try { window.GiveawayGator.initialize(); } catch (_) {}
          }
        }, 300);
      }
    } catch (_) {
      // swallow
    }
  }

  function updateUrl(params) {
    const url = new URL(window.location.href);
    Object.keys(params).forEach((k) => {
      const v = params[k];
      if (v === null || v === undefined || v === '') url.searchParams.delete(k);
      else url.searchParams.set(k, v);
    });
    history.pushState({}, '', url.toString());
  }

  const state = {
    page: 1,
    afterStack: [null], // cursor for each page start (page 1 -> null)
    hasNextPage: false,
  };

  function getSortOptions() {
    return [
      { value: 'relevance', label: 'Relevance' },
      { value: 'best-selling', label: 'Best selling' },
      { value: 'price-ascending', label: 'Price, low to high' },
      { value: 'price-descending', label: 'Price, high to low' },
      { value: 'created-descending', label: 'Date, new to old' },
      { value: 'created-ascending', label: 'Date, old to new' },
    ];
  }

  function ensureSortOptions() {
    const selects = [
      document.getElementById('SortBy'),
      document.getElementById('SortBy-mobile')
    ].filter(Boolean);
    if (!selects.length) return;
    const current = getParam('sort_by') || 'created-descending';
    const options = getSortOptions();
    selects.forEach((sel) => {
      if (!sel.options || sel.options.length === 0) {
        options.forEach(({ value, label }) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = label;
          sel.appendChild(opt);
        });
      }
      sel.value = current;
    });
  }

  async function runSearch(options = {}) {
    const append = Boolean(options.append);
    if (!window.StorefrontClient || !window.StorefrontClient.request) return;
    // Normalize incoming URLs (e.g., from predictive search links)
    sanitizeSearchUrl();
    const desiredPage = Math.max(1, parseInt(getParam('page') || '1', 10));
    const terms = (getParam('q') || '').trim();
    if (!terms) return; // nothing to search

    // Prefer existing sort_by param to match UI state
    const { sortKey, reverse } = mapSort(getParam('sort_by') || 'created-descending');

    // Minimal product search query; we will expand fields later when rendering UI.
    const QUERY = /* GraphQL */ `
      query SearchProducts($query: String!, $first: Int!, $sortKey: ProductSortKeys, $reverse: Boolean, $after: String) {
        products(query: $query, first: $first, sortKey: $sortKey, reverse: $reverse, after: $after) {
          edges {
            cursor
            node {
              id
              handle
              title
              vendor
              availableForSale
              featuredImage { url altText width height }
              priceRange { minVariantPrice { amount currencyCode } }
            }
          }
          pageInfo { hasNextPage }
        }
      }
    `;

    // Pagination cursor handling
    if (!append) {
      state.page = 1;
      state.afterStack = [null];
    }
    const after = append ? (state.afterStack[state.page] || null) : (state.afterStack[0] || null);
    const variables = { query: terms, first: PAGE_SIZE, sortKey, reverse, after };
    setLoading(true);
    const res = await window.StorefrontClient.request(QUERY, variables).catch((err) => {
      console.warn('[Search-GraphQL] query failed', err);
      // Show Liquid fallback if GraphQL fails
      try {
        const fallback = document.getElementById('product-grid');
        if (fallback) fallback.hidden = false;
        const wrapper = document.getElementById('product-grid-graphql');
        if (wrapper) wrapper.hidden = true;
      } catch (_) {}
      return null;
    });
    setLoading(false);
    if (!res || !res.data) return;

    // Exclude vendor 're:do' and optionally sold out items per theme setting
    const edges = Array.isArray(res.data.products?.edges) ? res.data.products.edges : [];
    let products = edges.map((e) => e.node).filter((p) => p && p.vendor !== 're:do');
    if (window.__SF_API__?.hideSoldOut) {
      products = products.filter((p) => p.availableForSale);
    }
    state.hasNextPage = Boolean(res.data.products?.pageInfo?.hasNextPage);
    // Store last cursor for current page
    const lastCursor = edges.length ? edges[edges.length - 1].cursor : null;
    state.afterStack[state.page] = lastCursor; // next page start

    // Verbose console output for debugging.
    if (window.__SF_API__?.debug) {
      console.debug('[Search-GraphQL] results', { count: products.length, products: products.map((p) => p.handle) });
    }

    // Render into GraphQL grid container using existing classes for maximum style parity.
    const grid = document.getElementById('graphql-product-list');
    const wrapper = document.getElementById('product-grid-graphql');
    if (grid && wrapper) {
      if (!append) grid.innerHTML = '';
      products.forEach((p) => {
        const li = document.createElement('li');
        li.className = 'grid__item';
        li.setAttribute('role', 'listitem');
        const card = document.createElement('div');
        card.className = 'card-wrapper product-card-wrapper underline-links-hover';

        const cardInner = document.createElement('div');
        const imageShape = wrapper.getAttribute('data-image-shape');
        cardInner.className = `card card--${window.themeSettings?.card_style || 'standard'} ${p.featuredImage ? 'card--media' : 'card--text'} ${imageShape && imageShape !== 'default' ? 'card--shape' : ''} card--extend-height`;

        const mediaWrap = document.createElement('div');
        // Calculate ratio similar to Liquid snippet
        const ratio = 1; // We'll compute based on settings below
        mediaWrap.className = 'card__inner ratio';
        // Determine ratio percent from section setting
        const imageRatio = wrapper.getAttribute('data-image-ratio');
        let ratioPercent = 100; // default square
        if (p.featuredImage && imageRatio === 'portrait') {
          ratioPercent = Math.round((1 / 0.7) * 100);
        } else if (p.featuredImage && imageRatio === 'adapt') {
          const ar = (p.featuredImage.width && p.featuredImage.height) ? (p.featuredImage.width / p.featuredImage.height) : 1;
          ratioPercent = Math.round((1 / (ar || 1)) * 100);
        }
        mediaWrap.style.setProperty('--ratio-percent', `${ratioPercent}%`);

        if (p.featuredImage?.url) {
          const media = document.createElement('div');
          const imageShape = wrapper.getAttribute('data-image-shape');
          media.className = `card__media${imageShape && imageShape !== 'default' ? ' shape--' + imageShape : ''}`;
          const mediaInner = document.createElement('div');
          mediaInner.className = 'media media--transparent media--hover-effect';
          const img = document.createElement('img');
          img.src = p.featuredImage.url;
          img.alt = p.featuredImage.altText || p.title;
          img.loading = 'lazy';
          img.className = 'motion-reduce product-image';
          if (p.featuredImage.width) img.width = p.featuredImage.width;
          if (p.featuredImage.height) img.height = p.featuredImage.height;
          mediaInner.appendChild(img);
          media.appendChild(mediaInner);
          mediaWrap.appendChild(media);
        }

        const titleWrap1 = document.createElement('div');
        titleWrap1.className = 'card__content';
        const info1 = document.createElement('div');
        info1.className = 'card__information';
        const h3a = document.createElement('h3');
        h3a.className = 'card__heading';
        const linkA = document.createElement('a');
        linkA.href = `/products/${p.handle}`;
        linkA.className = 'full-unstyled-link';
        linkA.textContent = p.title;
        h3a.appendChild(linkA);
        info1.appendChild(h3a);
        // badges: add sold out flag
        const badgesTop = document.createElement('div');
        badgesTop.className = 'card__badge ' + (window.themeSettings?.badge_position || 'bottom left');
        if (!p.availableForSale) {
          const sold = document.createElement('div');
          sold.className = 'label soldout';
          badgesTop.appendChild(sold);
        }
        titleWrap1.appendChild(info1);
        titleWrap1.appendChild(badgesTop);

        const titleWrap2 = document.createElement('div');
        titleWrap2.className = 'card__content';
        const info2 = document.createElement('div');
        info2.className = 'card__information';
        const h3b = document.createElement('h3');
        h3b.className = 'card__heading h5';
        const linkB = document.createElement('a');
        linkB.href = `/products/${p.handle}`;
        linkB.className = 'full-unstyled-link';
        linkB.textContent = p.title;
        h3b.appendChild(linkB);
        info2.appendChild(h3b);

        // simple price display (min variant price)
        const price = document.createElement('div');
        price.className = 'price';
        const priceContainer = document.createElement('div');
        priceContainer.className = 'price__container';
        const priceRegular = document.createElement('div');
        priceRegular.className = 'price__regular';
        const priceItem = document.createElement('span');
        priceItem.className = 'price-item price-item--regular';
        const amount = p.priceRange?.minVariantPrice?.amount;
        const currency = p.priceRange?.minVariantPrice?.currencyCode || 'USD';
        try {
          priceItem.textContent = new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(amount));
        } catch (_) {
          priceItem.textContent = `${amount} ${currency}`;
        }
        priceRegular.appendChild(priceItem);
        priceContainer.appendChild(priceRegular);
        price.appendChild(priceContainer);
        const ci = document.createElement('div');
        ci.className = 'card-information';
        ci.appendChild(price);

        // Giveaway Gator entry tag (parity with Liquid card)
        const gg = document.createElement('div');
        gg.className = 'gg-entry-tag';
        gg.setAttribute('data-gg-entry-tag', '');
        gg.setAttribute('data-product-id', getNumericProductId(p.id));
        gg.setAttribute('data-price', String(amount ?? ''));
        const ggTotal = document.createElement('span');
        ggTotal.className = 'gg-entry-total';
        ggTotal.textContent = '0';
        const ggBonus = document.createElement('span');
        ggBonus.className = 'gg-entry-bonus';
        gg.appendChild(ggTotal);
        gg.appendChild(ggBonus);
        ci.appendChild(gg);
        info2.appendChild(ci);

        titleWrap2.appendChild(info2);

        mediaWrap.appendChild(titleWrap1);
        cardInner.appendChild(mediaWrap);
        cardInner.appendChild(titleWrap2);
        card.appendChild(cardInner);
        li.appendChild(card);
        grid.appendChild(li);
      });

      // Show GraphQL grid and hide server grid to avoid duplication.
      wrapper.hidden = false;
      const serverGrid = document.getElementById('product-grid');
      if (serverGrid) serverGrid.style.display = 'none';

      renderLoadMore();
      reinitGiveawayGator();
      if (append) {
        // We successfully appended another page, move page pointer forward
        state.page += 1;
        updateUrl({ page: String(state.page) });
      }
      // If URL asked for a later page (via back/forward), incrementally load until reached
      if (!append && desiredPage > 1 && state.hasNextPage) {
        const loadNext = async () => {
          if (state.page >= desiredPage || !state.hasNextPage) return;
          await runSearch({ append: true });
          if (state.page < desiredPage && state.hasNextPage) return loadNext();
        };
        await loadNext();
      } else if (!append) {
        // Ensure page=1 is present in URL on first load
        updateUrl({ page: '1' });
      }
      // Update live region for screen readers
      updateLiveRegion(`${products.length} products ${append ? 'added' : 'loaded'}. Page ${state.page}.`);
    }
  }

  // Run connectivity test early when debug is enabled to validate configuration.
  if (window.__SF_API__?.debug && window.StorefrontClient?.test) {
    window.StorefrontClient.test().then(() => {
      console.debug('[Search-GraphQL] connectivity OK');
    }).catch((err) => {
      console.warn('[Search-GraphQL] connectivity failed', err);
    });
  }

  // Wire sort dropdowns to re-run search without full page reload.
  function bindSort() {
    ensureSortOptions();
    const selects = [
      document.getElementById('SortBy'),
      document.getElementById('SortBy-mobile')
    ].filter(Boolean);
    selects.forEach((sel) => {
      sel.addEventListener('change', () => {
        const val = sel.value;
        // Control URL params ourselves in GraphQL mode
        updateUrl({ sort_by: val });
        // Reset paging when sort changes
        state.page = 1;
        state.afterStack = [null];
        runSearch();
      });
    });
    // Intercept the search form submission to avoid Liquid-driven param shaping
    const form = document.querySelector('main-search form.search');
    if (form) {
      form.addEventListener('submit', (e) => {
        if (window.__SF_API__?.token) {
          e.preventDefault();
          const q = new FormData(form).get('q') || '';
          updateUrl({ q: String(q), page: '1' });
          state.page = 1;
          state.afterStack = [null];
          runSearch();
        }
      });
      // Also live-update results as the user types on the search page
      const input = form.querySelector('input[type="search"]');
      if (input) {
        input.addEventListener('input', debounce(() => {
          if (!window.__SF_API__?.token) return;
          const q = input.value || '';
          updateUrl({ q: String(q), page: '1' });
          state.page = 1;
          state.afterStack = [null];
          runSearch();
        }, 350));
      }
    }
    window.addEventListener('popstate', () => runSearch());
  }

  function renderLoadMore() {
    const wrapper = document.getElementById('product-grid-graphql');
    if (!wrapper) return;
    let btn = wrapper.querySelector('[data-graphql-load-more]');
    if (!btn) {
      btn = document.createElement('button');
      btn.setAttribute('data-graphql-load-more', '');
      btn.className = 'button';
      btn.style.display = 'block';
      btn.style.margin = '24px auto 0';
      btn.setAttribute('aria-controls', 'graphql-product-list');
      btn.setAttribute('aria-label', 'Load more products');
      wrapper.appendChild(btn);
    }
    if (!state.hasNextPage) {
      btn.remove();
      return;
    }
    btn.textContent = 'Load more';
    btn.disabled = false;
    btn.setAttribute('aria-disabled', 'false');
    btn.onclick = () => {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      runSearch({ append: true }).finally(() => {
        // Re-enable if there are more pages
        btn.disabled = !state.hasNextPage;
        btn.setAttribute('aria-disabled', String(!state.hasNextPage));
      });
    };
  }

  function ensureLiveRegion() {
    let region = document.getElementById('graphql-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'graphql-live-region';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.style.position = 'absolute';
      region.style.width = '1px';
      region.style.height = '1px';
      region.style.overflow = 'hidden';
      region.style.clip = 'rect(1px, 1px, 1px, 1px)';
      document.body.appendChild(region);
    }
    return region;
  }

  function updateLiveRegion(text) {
    const region = ensureLiveRegion();
    region.textContent = String(text || '');
  }

  // Kick off initial render
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      bindSort();
      runSearch();
    });
  } else {
    bindSort();
    runSearch();
  }
})();

